<template>
  <div class="create-project">
    <!-- Loader overlay -->
    <v-overlay :value="!isLoaded">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>

    <template v-if="isLoaded">
      <v-container>
        <project-form
          :project="project"
          :patient="patient"
          :disable-product-btn="!isProductCreated"
          @on-cancel="dialog = true"
          @on-submit="saveProject"
          @on-product-click="goToProductPage(project)"
        />
      </v-container>

      <!-- Confirmation dialog -->
      <v-dialog
        v-model="dialog"
        max-width="290"
      >
        <v-card>
          <v-card-title class="headline">
            Annulation
          </v-card-title>
          <v-card-text>Toutes modifications seront perdues !</v-card-text>
          <v-card-actions>
            <div class="flex-grow-1" />
            <v-btn
              color="red"
              to="/"
              dark
              text
            >
              Oui
            </v-btn>
            <v-btn
              text
              color="black"
              @click="dialog = false"
            >
              Non
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
const uuidv4 = require("uuid/v4");
import { mapGetters } from "vuex";
import { productsObject } from "@/utils";
import ErrorHandlingMixin from "@/mixins/ErrorHandlingMixin";

import ProjectForm from "./components/ProjectForm";

export default {
  name: "CreateProject",
  components: {
    ProjectForm
  },

  mixins: [ErrorHandlingMixin],

  data() {
    return {
      isLoaded: false,
      patient: {},
      project: JSON.parse(JSON.stringify(this.$store.state.projects.project)),

      isProductCreated: false,

      dialog: false
    };
  },

  computed: {
    ...mapGetters({
      getLoggedInUserId: "auth/getLoggedInUserId"
    })
  },

  created() {
    this.init();
  },

  beforeDestroy() {
    this.$store.dispatch("projects/resetProject");
  },

  methods: {
    // Init
    async init() {
      this.isLoaded = false;
      this.$store.dispatch("projects/resetProject");

      // Get user from local db
      const user = await this.$db.user.toCollection().first();
      this.project.applicator_id = user.user.id;
      this.isLoaded = true;
    },

    // Save a complete project in local db
    async saveProject() {
      try {
        // Create patient
        const patient = await this.createPatient(this.patient);

        const localProject = await this.createProject(this.project, patient);

        await this.createProduct(localProject);

        this.isProductCreated = true;

        this.$notify({
          group: "main",
          type: "success",
          title: "Succès",
          text: "Projet créé",
          duration: 5000
        });

        this.$router.push({
          name: "ProjectPage",
          params: { projectUuid: localProject.uuid }
        });
      } catch (error) {
        this.handleError(error);
        console.log("Erreur: ", error.response);
      }
    },

    // Create patient in local db
    async createPatient(patient) {
      try {
        patient.uuid = uuidv4();
        patient.sync = 0;
        patient.fullname = patient.firstname + " " + patient.lastname;

        const patientKey = await this.$db.patients.add(patient);

        return await this.$db.patients.get(patientKey);
      } catch (error) {
        throw new Error("Le patient n'a pas pu être enregistré");
      }
    },

    // Create project in local db
    async createProject(project, patient) {
      try {
        project.uuid = uuidv4();
        project.patient_uuid = patient.uuid;
        project.patient = patient;
        project.sync = 0;

        const today = new Date();
        project.created_at = this.formatDate(today, "dd/mm/yyyy H:m");

        const projectKey = await this.$db.projects.add(project);

        return await this.$db.projects.get(projectKey);
      } catch (error) {
        throw new Error("Le project n'a pas pu être enregistré");
      }
    },

    formatDate(date, format) {
      const map = {
        mm: ("0" + (date.getMonth() + 1)).slice(-2),
        dd: ("0" + date.getDate()).slice(-2),
        yy: date
          .getFullYear()
          .toString()
          .slice(-2),
        yyyy: date.getFullYear(),

        H: date.getHours(),
        m: date.getMinutes()
      };

      return format.replace(/mm|dd|yy|yyy|H|m/gi, matched => map[matched]);
    },

    // Create product in local db
    async createProduct(project) {
      try {
        const productTableName =
          productsObject[project.product_model].tableName;
        const productModuleName =
          productsObject[project.product_model].productModuleName;
        const resetProductFormAction =
          productsObject[project.product_model].resetProductForm;

        // Set default product form
        await this.$store.dispatch(resetProductFormAction);

        const localProduct = this.$store.state[productModuleName][
          productModuleName
        ];

        // Set required props
        localProduct.uuid = uuidv4();
        localProduct.projects_uuid = project.uuid;
        localProduct.sync = 0;
        localProduct.user_id = this.getLoggedInUserId;

        // Add product to IndexedDB
        const localProductKey = await this.$db[productTableName].add(
          localProduct
        );

        // Update project
        project.product_uuid = localProductKey;
        project.local_version = localProductKey;
        await this.$db.projects.put(project);
      } catch (error) {
        throw new Error("Le produit n'a pas pu être enregistré");
      }
    }
  }
};
</script>

<style></style>
